import { useEffect, useRef, type MutableRefObject } from "react";
import {
  useAudioVideo,
  useMeetingManager,
  useRosterState,
  useActiveSpeakersState,
  useToggleLocalMute,
} from "amazon-chime-sdk-component-library-react";
import type { VideoPriorityBasedPolicy } from "amazon-chime-sdk-js";
import { usePlatformStore } from "@platform/store";
import { ChimeObserverState, type ChimeObserverStore } from "./chimeObserverState";
import { useDeviceStateBroadcast } from "./useDeviceStateBroadcast";

// Re-export for any consumers that import from this module
export { resolveAttendeeTileState } from "./resolveAttendeeTileState";

const DEVICE_STATE_TOPIC = "device-state";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Unified Chime observer hook. Registers one AudioVideoObserver, one
 * VideoDownlinkObserver, one data message subscription, and consumes
 * roster + active speaker hooks. All SDK events are translated into
 * `attendeeTileState` in Zustand.
 *
 * Delegates all state-machine logic to ChimeObserverState. This hook
 * is responsible only for React wiring: SDK hooks, effects, and cleanup.
 */
export function useChimeObserver(
  policyRef: MutableRefObject<VideoPriorityBasedPolicy>
) {
  const audioVideo = useAudioVideo();
  const meetingManager = useMeetingManager();
  const { roster } = useRosterState();
  const activeSpeakers = useActiveSpeakersState();
  const { muted } = useToggleLocalMute();
  const hasLocalVideo = usePlatformStore(
    (s) => s.videoSourceState.hasLocalVideo
  );
  const rosterSize = Object.keys(roster).length;

  // Synchronous roster ref for active speaker sync
  const rosterRef = useRef(roster);
  useEffect(() => {
    rosterRef.current = roster;
  }, [roster]);

  // --- State machine instance (lazy-init, stable across renders) ---

  const stateRef = useRef<ChimeObserverState | null>(null);
  if (!stateRef.current) {
    stateRef.current = new ChimeObserverState(
      () => {
        const s = usePlatformStore.getState();
        return {
          attendeeTileState: s.attendeeTileState,
          videoSourceState: s.videoSourceState,
          layout: s.layout,
          user: s.user,
          setAttendeeTileState: s.setAttendeeTileState,
          setAttendeeTileStates: s.setAttendeeTileStates,
          removeAttendeeTileState: s.removeAttendeeTileState,
          setConnectionQuality: s.setConnectionQuality,
          setActiveSpeakers: s.setActiveSpeakers,
          setRoster: s.setRoster,
          setState: usePlatformStore.setState,
        } satisfies ChimeObserverStore;
      },
      () =>
        meetingManager.meetingSession?.configuration.credentials?.attendeeId ||
        null
    );
  }
  const state = stateRef.current;

  // -------------------------------------------------------------------------
  // Effect 1: Roster sync
  // -------------------------------------------------------------------------

  useEffect(() => {
    state.syncRoster(roster, state.getLocalAttendeeId());
  }, [roster, state]);

  // -------------------------------------------------------------------------
  // Effect 2: Active speaker sync
  // -------------------------------------------------------------------------

  useEffect(() => {
    state.syncActiveSpeakers(
      activeSpeakers,
      rosterRef.current,
      state.getLocalAttendeeId()
    );
  }, [activeSpeakers, state]);

  // Cleanup active speakers on unmount
  useEffect(() => {
    return () => {
      usePlatformStore.getState().setActiveSpeakers([]);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Effect 3a: Synchronous presence subscription (roster departures)
  // -------------------------------------------------------------------------

  useEffect(() => {
    // Use the raw SDK audioVideo (not the component library proxy) because
    // the proxy may not expose realtimeUnsubscribeToAttendeeIdPresence.
    const rawAudioVideo = meetingManager.meetingSession?.audioVideo;
    if (!rawAudioVideo) return;
    return state.subscribeToPresence(rawAudioVideo);
  }, [meetingManager.meetingSession, state]);

  // -------------------------------------------------------------------------
  // Effect 3b: AudioVideoObserver + VideoDownlinkObserver registration
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!audioVideo) return;

    const policy = policyRef.current;
    const observer = state.createAudioVideoObserver(policyRef);
    const downlinkObserver = state.createDownlinkObserver(audioVideo);

    audioVideo.addObserver(observer);
    policy.addObserver(downlinkObserver);

    return () => {
      audioVideo.removeObserver(observer);
      policy.removeObserver(downlinkObserver);
      state.clearReconnectGuard();
      if (state.massDropTimer) {
        clearTimeout(state.massDropTimer);
        state.massDropTimer = null;
      }
      if (state.recoveryTimer) {
        clearTimeout(state.recoveryTimer);
        state.recoveryTimer = null;
      }
    };
  }, [audioVideo, state, policyRef]);

  // -------------------------------------------------------------------------
  // Effect 4: Data message subscription (receive intent)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (!audioVideo) return;

    const handler = (message: any) => {
      state.handleDataMessage(message);
    };

    audioVideo.realtimeSubscribeToReceiveDataMessage(
      DEVICE_STATE_TOPIC,
      handler
    );
    return () => {
      audioVideo.realtimeUnsubscribeFromReceiveDataMessage(DEVICE_STATE_TOPIC);
    };
  }, [audioVideo, meetingManager.meetingSession, state]);

  // -------------------------------------------------------------------------
  // Effect 5: Device state broadcast (extracted hook)
  // -------------------------------------------------------------------------

  useDeviceStateBroadcast(audioVideo, hasLocalVideo, muted, rosterSize);

  // -------------------------------------------------------------------------
  // Cleanup all timers on unmount
  // -------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      state.dispose();
    };
  }, [state]);
}
